import React, { createContext, lazy, useEffect, useMemo } from "react";
import { Route, Switch, Redirect, useHistory } from "react-router";
import { CircularProgress, Typography } from "@material-ui/core";
import { useCurrentUser } from "data/user";
import { routes } from "./routes";
import Layout from "components/Layout";
import canActAsTechnician from "../utils/canActAsTechnician";
import canActAsProjectManager from "../utils/canActAsProjectManager";
import { ProjectManagerContext } from "./ProjectManagerContext";
import CompleteInvite from "./Login/CompleteInvite";
import { useAuth } from "data/auth/hooks";
import TermsConsent from "components/TermsConsent";

const AdminLayout = lazy(() => import("ui/Admin"));
const TechLayout = lazy(() => import("ui/Techician"));
const NoProjectsLayout = lazy(() => import("ui/NoProjects"));
const EmailChange = lazy(() => import("ui/Users/EmailChangeConfirmation/index"));

const AuthenticatedApp: React.FC = () => {
  const [{ user, isLoadingUser }, { getCurrentUser }] = useCurrentUser();
  const canViewAsProjectManager = useMemo(() => (user ? canActAsProjectManager(user) : false), [user]);
  const canViewAsTechnician = useMemo(() => (user ? canActAsTechnician(user) : false), [user]);
  const isRegistered = useMemo(() => user?.isRegistered, [user]);
  const history = useHistory();
  const [, { getUserRegistration }] = useAuth();

  const defaultPath = useMemo(() => {
    if (canViewAsProjectManager) {
      return routes.admin.path;
    } else if (canViewAsTechnician) {
      return routes.tech.path;
    } else {
      return routes.root.path;
    }
  }, [canViewAsProjectManager, canViewAsTechnician, isRegistered]);

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  useEffect(() => {
    const fetchRegistration = async () => {
      if (user && !isRegistered) {
        try {
          // fetch the token asynchronously
          const hardcodedToken = await getUserRegistration(user.id!);
          history.push(`/signup/${hardcodedToken}`);
        } catch (error) {
          console.error("Error fetching registration token:", error);
        }
      }
    };

    fetchRegistration();
  }, [isRegistered]);

  if (isLoadingUser) {
    return <CircularProgress />;
  }

  if (!user) {
    return <CircularProgress />;
  }

  return (
    <React.Suspense fallback={<CircularProgress />}>
      <TermsConsent/>
      <Switch>
        <ProjectManagerContext.Provider value={canViewAsProjectManager}>
          <Route
            path={routes.users.confirm.id.path}
            render={() => (
              <Layout showSidebar>
                <EmailChange />
              </Layout>
            )}
          />
          {canViewAsProjectManager && <Route path={routes.admin.path} component={AdminLayout} />}
          {canViewAsTechnician && <Route path={routes.tech.path} component={TechLayout} />}
          {!canViewAsProjectManager && !canViewAsTechnician && <Route component={NoProjectsLayout} />}
          {!isRegistered && <Route exact path={routes.signup.verificationToken.path} component={CompleteInvite} />}
          {isRegistered && <Redirect to={defaultPath} />}
        </ProjectManagerContext.Provider>
      </Switch>
    </React.Suspense>
  );
};

export default AuthenticatedApp;
